import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Play, Pause, SkipForward, SkipBack, Music2, Zap, ExternalLink } from "lucide-react";
import { BPMSyncIndicator } from "./BPMSyncIndicator";
import type { Track } from "@/services/music-match-service";
import { useRef, useEffect } from "react";

interface MusicPlayerProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  currentBPM: number;
  autoSync: boolean;
  isSynced: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onAutoSyncToggle: (enabled: boolean) => void;
  spotifyReady?: boolean;
  position?: number;
  duration?: number;
}

export const MusicPlayer = ({
  currentTrack,
  isPlaying,
  currentBPM,
  autoSync,
  isSynced,
  onPlayPause,
  onNext,
  onPrevious,
  onAutoSyncToggle,
  spotifyReady = false,
  position = 0,
  duration = 0,
}: MusicPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle preview playback (only if Spotify SDK not ready)
  useEffect(() => {
    if (spotifyReady) return;
    if (!audioRef.current || !currentTrack?.preview_url) return;

    if (isPlaying) {
      audioRef.current.play().catch(err => console.error('Preview play error:', err));
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, currentTrack, spotifyReady]);

  // Update audio source when track changes (only for preview mode)
  useEffect(() => {
    if (spotifyReady) return;
    if (audioRef.current && currentTrack?.preview_url) {
      audioRef.current.src = currentTrack.preview_url;
      audioRef.current.load();
    }
  }, [currentTrack?.id, spotifyReady]);
  return (
    <Card className="bg-card/95 backdrop-blur-md border-border/50">
      <CardContent className="p-4 space-y-4">
        {/* Track Info */}
        <div className="flex items-center gap-3">
          {currentTrack ? (
            <>
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {currentTrack.album_art ? (
                  <img 
                    src={currentTrack.album_art} 
                    alt={currentTrack.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Music2 className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{currentTrack.name}</p>
                <p className="text-xs text-muted-foreground truncate">{currentTrack.artist}</p>
                {currentTrack.external_url && (
                  <a 
                    href={currentTrack.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-green-500 hover:text-green-600 mt-0.5"
                  >
                    Abrir no Spotify
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {/* Hidden audio element for preview playback */}
              {currentTrack.preview_url && (
                <audio 
                  ref={audioRef}
                  onEnded={() => onNext()}
                  className="hidden"
                />
              )}
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <Music2 className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Nenhuma música tocando</p>
              </div>
            </>
          )}
        </div>

        {/* BPM Sync Indicator */}
        {currentTrack && currentBPM > 0 && (
          <BPMSyncIndicator 
            currentBPM={currentBPM} 
            trackBPM={currentTrack.bpm || 0}
            isSynced={isSynced}
          />
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onPrevious}
            disabled={!currentTrack}
            className="h-10 w-10"
          >
            <SkipBack className="w-5 h-5" />
          </Button>
          
          <Button
            size="icon"
            onClick={onPlayPause}
            disabled={!currentTrack}
            className="h-12 w-12 bg-gradient-to-br from-primary to-accent hover:opacity-90"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" fill="white" />
            ) : (
              <Play className="w-6 h-6" fill="white" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onNext}
            disabled={!currentTrack}
            className="h-10 w-10"
          >
            <SkipForward className="w-5 h-5" />
          </Button>
        </div>

        {/* Auto-Sync Toggle */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className={`w-4 h-4 ${autoSync ? 'text-primary' : 'text-muted-foreground'}`} />
            <Label htmlFor="auto-sync" className="text-sm font-medium cursor-pointer">
              Sincronização BPM
            </Label>
          </div>
          <Switch
            id="auto-sync"
            checked={autoSync}
            onCheckedChange={onAutoSyncToggle}
          />
        </div>

        {/* Progress bar (Spotify SDK only) */}
        {spotifyReady && duration > 0 && (
          <div className="space-y-1">
            <Slider
              value={[position]}
              max={duration}
              step={1000}
              className="cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatTime(position)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}

        {/* Track BPM Info */}
        {currentTrack && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Música: {currentTrack.bpm || '--'} BPM</span>
            <span>Seu BPM: {currentBPM || '--'}</span>
            {spotifyReady && (
              <span className="text-green-500 font-medium">● Spotify Conectado</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};